import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Calendar, RefreshCw } from "lucide-react";
import { ApplyPresetResponse } from "../../features/organization/contracts/ApplyPresetResponse";
import { PresetItemResponse } from "../../features/organization/contracts/PresetItemResponse";
import { ROUTES } from "../../routes";
import { calendarWeekPath } from "./fahrschulePresetDefaults";

interface PresetResultPanelProps {
  result: ApplyPresetResponse;
  busy: boolean;
  /** The first theory date ("YYYY-MM-DD"): the calendar link opens on its week. */
  calendarDate: string;
  onTryAgain: () => void;
}

/** An expandable list of item names under a count ("Already there: 9"). */
const ItemList: React.FC<{ label: string; items: PresetItemResponse[] }> = ({ label, items }) => {
  if (items.length === 0) return null;
  return (
    <details className="mt-2">
      <summary className="cursor-pointer">{label}</summary>
      <ul className="mt-1 list-disc pl-5">
        {items.map((item, index) => (
          <li key={`${item.kind}-${index}`}>{item.name}</li>
        ))}
      </ul>
    </details>
  );
};

/**
 * What the last preset run did: created and skipped items by name, failed items with a
 * "Try again" that re-posts the same request, and a link to the first theory week.
 */
const PresetResultPanel: React.FC<PresetResultPanelProps> = ({ result, busy, calendarDate, onTryAgain }) => {
  const { t } = useTranslation();
  const failed = result.issues.length > 0;
  const tone = failed
    ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
    : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";

  return (
    <div role={failed ? "alert" : "status"} className={`mt-4 rounded-xl p-3 text-sm ${tone}`}>
      {failed && (
        <>
          <p className="font-medium">{t("preset.fahrschule.issuesTitle")}</p>
          <ul className="mt-1 list-disc pl-5">
            {result.issues.map((issue) => (
              <li key={issue}>{issue}</li>
            ))}
          </ul>
        </>
      )}
      <ItemList label={t("preset.fahrschule.createdCount", { n: result.created.length })} items={result.created} />
      <ItemList label={t("preset.fahrschule.skippedCount", { n: result.skipped.length })} items={result.skipped} />
      <div className="mt-3 flex flex-wrap gap-2">
        {failed && (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onTryAgain}>
            <RefreshCw size={16} />
            <span>{t("preset.fahrschule.tryAgain")}</span>
          </button>
        )}
        <Link to={calendarWeekPath(ROUTES.CALENDAR, calendarDate)} className="btn btn-secondary">
          <Calendar size={16} />
          <span>{t("preset.fahrschule.seeCalendar")}</span>
        </Link>
      </div>
    </div>
  );
};

export default PresetResultPanel;
